const STORAGE_PREFIX = 'bangerfy_key_';
const SESSION_PREFIX = 'bangerfy_key_session_';
const PROVIDER_KEY = 'bangerfy_active_provider';
const MODEL_PREFIX = 'bangerfy_model_';
const AUTH_MODE_KEY = 'bangerfy_auth_mode';
const GEMINI_AUTH_KEY = 'bangerfy_gemini_auth';
const GEMINI_METHOD_KEY = 'bangerfy_gemini_method';

export type KeyStorage = 'session' | 'local';

export type AIProvider = 'openai' | 'gemini' | 'anthropic';

export type AuthMode = 'byok' | 'subscription';

export type GeminiMethod = 'apikey' | 'google';

export interface GeminiAuthInfo {
  email: string;
  accessToken: string;
  refreshToken?: string;
  expiresAt: number;
}

export interface ModelOption {
  id: string;
  name: string;
  description: string;
  tier: 'free' | 'standard' | 'premium';
}

export interface ProviderConfig {
  id: AIProvider;
  name: string;
  keyPrefix: string;
  keyPlaceholder: string;
  keyUrl: string;
  keyUrlLabel: string;
  capabilities: ('chat' | 'transcription')[];
  models: ModelOption[];
  defaultModel: string;
}

export const PROVIDERS: Record<AIProvider, ProviderConfig> = {
  openai: {
    id: 'openai',
    name: 'OpenAI',
    keyPrefix: 'sk-',
    keyPlaceholder: 'sk-...',
    keyUrl: 'https://platform.openai.com/api-keys',
    keyUrlLabel: 'platform.openai.com/api-keys',
    capabilities: ['chat', 'transcription'],
    defaultModel: 'gpt-4o-mini',
    models: [
      { id: 'gpt-5.2', name: 'GPT-5.2', description: 'Latest flagship', tier: 'premium' },
      { id: 'o3-pro', name: 'o3-pro', description: 'Reasoning, top-tier', tier: 'premium' },
      { id: 'o3', name: 'o3', description: 'Reasoning, high accuracy', tier: 'premium' },
      { id: 'gpt-4.1', name: 'GPT-4.1', description: 'High quality, long context', tier: 'premium' },
      { id: 'gpt-5.2-mini', name: 'GPT-5.2 mini', description: 'Latest gen, fast', tier: 'standard' },
      { id: 'o4-mini', name: 'o4-mini', description: 'Reasoning, fast', tier: 'standard' },
      { id: 'gpt-4.1-mini', name: 'GPT-4.1 mini', description: 'Fast, balanced', tier: 'standard' },
      { id: 'gpt-4o', name: 'GPT-4o', description: 'Stable, general purpose', tier: 'standard' },
      { id: 'gpt-4o-mini', name: 'GPT-4o mini', description: 'Fast, low cost', tier: 'free' },
      { id: 'gpt-4.1-nano', name: 'GPT-4.1 nano', description: 'Ultra low cost, fastest', tier: 'free' },
    ],
  },
  gemini: {
    id: 'gemini',
    name: 'Google Gemini',
    keyPrefix: 'AI',
    keyPlaceholder: 'AIza...',
    keyUrl: 'https://aistudio.google.com/apikey',
    keyUrlLabel: 'aistudio.google.com/apikey',
    capabilities: ['chat'],
    defaultModel: 'gemini-2.0-flash',
    models: [
      { id: 'gemini-3.0-pro', name: 'Gemini 3.0 Pro', description: 'Latest flagship', tier: 'premium' },
      { id: 'gemini-3.0-flash', name: 'Gemini 3.0 Flash', description: 'Latest gen, fast', tier: 'standard' },
      { id: 'gemini-2.5-pro', name: 'Gemini 2.5 Pro', description: 'High quality, deep reasoning', tier: 'premium' },
      { id: 'gemini-2.5-flash', name: 'Gemini 2.5 Flash', description: 'Best value, reasoning', tier: 'standard' },
      { id: 'gemini-2.0-flash', name: 'Gemini 2.0 Flash', description: 'Fast, low cost', tier: 'free' },
      { id: 'gemini-2.0-flash-lite', name: 'Gemini 2.0 Flash Lite', description: 'Ultra light, lowest cost', tier: 'free' },
    ],
  },
  anthropic: {
    id: 'anthropic',
    name: 'Anthropic Claude',
    keyPrefix: 'sk-ant-',
    keyPlaceholder: 'sk-ant-...',
    keyUrl: 'https://console.anthropic.com/settings/keys',
    keyUrlLabel: 'console.anthropic.com/settings/keys',
    capabilities: ['chat'],
    defaultModel: 'claude-sonnet-4-6',
    models: [
      { id: 'claude-opus-4-6', name: 'Claude Opus 4.6', description: 'Top quality, advanced reasoning', tier: 'premium' },
      { id: 'claude-sonnet-4-6', name: 'Claude Sonnet 4.6', description: 'Balanced, optimal', tier: 'standard' },
      { id: 'claude-haiku-4-5-20251001', name: 'Claude Haiku 4.5', description: 'Ultra fast, low cost', tier: 'free' },
    ],
  },
};

function obfuscate(key: string): string {
  return btoa(key.split('').reverse().join(''));
}

function deobfuscate(encoded: string): string {
  try {
    return atob(encoded).split('').reverse().join('');
  } catch {
    return '';
  }
}

export const apiKeyService = {
  getKey(provider?: AIProvider): string {
    const p = provider || this.getActiveProvider();
    const sessionKey = sessionStorage.getItem(SESSION_PREFIX + p);
    if (sessionKey) return deobfuscate(sessionKey);

    const localKey = localStorage.getItem(STORAGE_PREFIX + p);
    if (localKey) return deobfuscate(localKey);

    return '';
  },

  setKey(key: string, storage: KeyStorage = 'local', provider?: AIProvider): void {
    const p = provider || this.getActiveProvider();
    const encoded = obfuscate(key);

    if (storage === 'local') {
      localStorage.setItem(STORAGE_PREFIX + p, encoded);
      sessionStorage.removeItem(SESSION_PREFIX + p);
    } else {
      sessionStorage.setItem(SESSION_PREFIX + p, encoded);
      localStorage.removeItem(STORAGE_PREFIX + p);
    }
  },

  clearKey(provider?: AIProvider): void {
    const p = provider || this.getActiveProvider();
    sessionStorage.removeItem(SESSION_PREFIX + p);
    localStorage.removeItem(STORAGE_PREFIX + p);
  },

  hasKey(provider?: AIProvider): boolean {
    return !!this.getKey(provider);
  },

  hasAnyKey(): boolean {
    return Object.keys(PROVIDERS).some(p => this.hasKey(p as AIProvider));
  },

  getStorageType(provider?: AIProvider): KeyStorage | null {
    const p = provider || this.getActiveProvider();
    if (sessionStorage.getItem(SESSION_PREFIX + p)) return 'session';
    if (localStorage.getItem(STORAGE_PREFIX + p)) return 'local';
    return null;
  },

  getActiveProvider(): AIProvider {
    return (localStorage.getItem(PROVIDER_KEY) as AIProvider) || 'openai';
  },

  setActiveProvider(provider: AIProvider): void {
    localStorage.setItem(PROVIDER_KEY, provider);
  },

  getAuthMode(): AuthMode {
    return (localStorage.getItem(AUTH_MODE_KEY) as AuthMode) || 'byok';
  },

  setAuthMode(mode: AuthMode): void {
    localStorage.setItem(AUTH_MODE_KEY, mode);
  },

  getProviderConfig(provider?: AIProvider): ProviderConfig {
    return PROVIDERS[provider || this.getActiveProvider()];
  },

  getSelectedModel(provider?: AIProvider): string {
    const p = provider || this.getActiveProvider();
    return localStorage.getItem(MODEL_PREFIX + p) || PROVIDERS[p].defaultModel;
  },

  setSelectedModel(modelId: string, provider?: AIProvider): void {
    const p = provider || this.getActiveProvider();
    localStorage.setItem(MODEL_PREFIX + p, modelId);
  },

  getModelOptions(provider?: AIProvider): ModelOption[] {
    const p = provider || this.getActiveProvider();
    return PROVIDERS[p].models;
  },

  getConfiguredProviders(): AIProvider[] {
    return (Object.keys(PROVIDERS) as AIProvider[]).filter(p => this.hasKey(p));
  },

  maskKey(key: string): string {
    if (key.length <= 8) return '••••••••';
    return key.slice(0, 5) + '•'.repeat(Math.min(key.length - 9, 20)) + key.slice(-4);
  },

  validateKeyFormat(key: string, provider?: AIProvider): boolean {
    const p = provider || this.getActiveProvider();
    const config = PROVIDERS[p];
    return key.startsWith(config.keyPrefix) && key.length >= 20;
  },

  // Gemini Google login
  getGeminiMethod(): GeminiMethod {
    return (localStorage.getItem(GEMINI_METHOD_KEY) as GeminiMethod) || 'apikey';
  },

  setGeminiMethod(method: GeminiMethod): void {
    localStorage.setItem(GEMINI_METHOD_KEY, method);
  },

  getGeminiAuth(): GeminiAuthInfo | null {
    const raw = localStorage.getItem(GEMINI_AUTH_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(deobfuscate(raw));
    } catch {
      return null;
    }
  },

  setGeminiAuth(auth: GeminiAuthInfo): void {
    localStorage.setItem(GEMINI_AUTH_KEY, obfuscate(JSON.stringify(auth)));
  },

  clearGeminiAuth(): void {
    localStorage.removeItem(GEMINI_AUTH_KEY);
  },

  isGeminiLoggedIn(): boolean {
    const auth = this.getGeminiAuth();
    if (!auth) return false;
    return auth.expiresAt > Date.now();
  },

  hasGeminiAccess(): boolean {
    if (this.getGeminiMethod() === 'google') {
      return this.isGeminiLoggedIn();
    }
    return this.hasKey('gemini');
  },
};
